import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { SearchSuggestions } from './SearchSuggestions';
import { Menu, X, Search, Users, Star, BarChart3, Stethoscope, Handshake, Bot, Wallet, Code, FileText, ArrowRightLeft, Network, Cloud, Linkedin, Instagram, Twitter, Shield, Brain, Tag } from 'lucide-react';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Lock the page behind the mobile menu so only the menu scrolls (position:fixed works on iOS Safari too).
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const y = window.scrollY;
    const path = window.location.pathname;
    const b = document.body.style;
    const prev = { position: b.position, top: b.top, width: b.width, overflow: b.overflow };
    Object.assign(b, { position: 'fixed', top: `-${y}px`, width: '100%', overflow: 'hidden' });
    return () => {
      Object.assign(b, prev);
      if (window.location.pathname === path) window.scrollTo(0, y);
    };
  }, [mobileMenuOpen]);
  const [navQuery, setNavQuery] = useState('');
  const [navFocus, setNavFocus] = useState(false);
  const [mobileQuery, setMobileQuery] = useState('');
  const [mobileFocus, setMobileFocus] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [navigationKey, setNavigationKey] = React.useState(0);

  // Close dropdowns when location changes
  React.useEffect(() => {
    setMobileMenuOpen(false);
    setResourcesOpen(false);
    setServicesOpen(false);
    // Force re-render of NavigationMenu to close dropdowns
    setNavigationKey(prev => prev + 1);
  }, [location.pathname]);

  const SECTION_PATHS = {
    marketplace: ['/marketplace', '/vendor/', '/emr/', '/emrs', '/compare'],
    clinics: ['/selection-guide', '/resources-guide', '/data-migration', '/hl7-integration', '/cloud-deployment', '/privacy-security'],
    vendors: ['/for-vendors', '/join'],
    services: ['/pricing', '/co-design-studio'],
    partner: ['/senzcare-ai'],
    about: ['/about'],
  };
  const isActive = (key) => SECTION_PATHS[key].some((p) => location.pathname === p || location.pathname.startsWith(p.endsWith('/') ? p : `${p}/`));
  const activeCls = (key) => (isActive(key) ? 'text-orange-600' : 'text-gray-700');

  const handleResourceClick = () => {
    setResourcesOpen(false);
  };

  const handleServiceClick = () => {
    setServicesOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleLogoClick = () => {
    setResourcesOpen(false);
    setServicesOpen(false);
    // Scroll to top of page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const getUserInitials = () => {
    if (user) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return 'U';
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 mr-2 sm:mr-3" onClick={handleLogoClick} data-testid="navbar-logo">
            <picture>
              <source srcSet="/cliniciq-logo.webp" type="image/webp" />
              <img 
                src="/cliniciq-logo-optimized.jpg" 
                alt="ClinicIQ Logo" 
                className="h-10 min-[360px]:h-[52px] sm:h-14 md:h-16 w-auto max-w-none object-contain"
                width="172"
                height="64"
              />
            </picture>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center space-x-4">
            <NavigationMenu key={navigationKey}>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <Link 
                    to="/marketplace" 
                    onClick={() => {
                      setResourcesOpen(false);
                      setServicesOpen(false);
                    }}
                    className={`inline-flex h-9 items-center px-2.5 text-sm font-medium whitespace-nowrap transition-colors hover:text-orange-600 ${
                      activeCls('marketplace')
                    }`}
                  >
                    Marketplace
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem value="resources">
                  <NavigationMenuTrigger
                    className={`px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors hover:text-orange-600 data-[state=open]:text-orange-600 ${isActive('clinics') ? 'text-orange-600' : ''}`}
                  >
                    For Clinics
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-96 max-h-[560px] overflow-y-auto p-4">
                      <div className="grid gap-2">
                        <Link to="/selection-guide" onClick={handleResourceClick} data-testid="nav-clinics-selection-guide" className="flex items-center space-x-3 p-3 rounded-lg bg-orange-50 border border-orange-100 hover:bg-orange-100 transition-colors cursor-pointer">
                          <BarChart3 className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm">Technology Selection Guide</div>
                            <div className="text-xs text-gray-500 truncate">6 quick questions, matched tools</div>
                          </div>
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-orange-700 bg-white rounded-full px-2 py-0.5 border border-orange-200">Get matched</span>
                        </Link>
                        <Link to="/resources-guide" onClick={handleResourceClick} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                          <FileText className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm">Medical Data Storage Guide</div>
                            <div className="text-xs text-gray-500 truncate">Storage solutions & compliance</div>
                          </div>
                        </Link>
                        <Link to="/data-migration" onClick={handleResourceClick} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                          <ArrowRightLeft className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm">EMR Data Migration Guide</div>
                            <div className="text-xs text-gray-500 truncate">Migration types & best practices</div>
                          </div>
                        </Link>
                        <Link to="/hl7-integration" onClick={handleResourceClick} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                          <Network className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm">HL7 & FHIR Integration</div>
                            <div className="text-xs text-gray-500 truncate">Standards-based EMR integration</div>
                          </div>
                        </Link>
                        <Link to="/cloud-deployment" onClick={handleResourceClick} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                          <Cloud className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm">On-Premise vs Hosted Cloud</div>
                            <div className="text-xs text-gray-500 truncate">Private cloud deployment options</div>
                          </div>
                        </Link>
                        <Link to="/privacy-security" onClick={handleResourceClick} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                          <Shield className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm">Privacy & Security Guide</div>
                            <div className="text-xs text-gray-500 truncate">Protecting health information</div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    to="/for-vendors" 
                    onClick={() => {
                      setResourcesOpen(false);
                      setServicesOpen(false);
                    }}
                    className={`inline-flex h-9 items-center px-2.5 text-sm font-medium whitespace-nowrap transition-colors hover:text-orange-600 ${
                      activeCls('vendors')
                    }`}
                  >
                    For Vendors
                  </Link>
                </NavigationMenuItem>

                {/* Services Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={`px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors hover:text-orange-600 data-[state=open]:text-orange-600 ${isActive('services') ? 'text-orange-600' : ''}`}
                  >
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-96 p-4">
                      <div className="grid gap-2">
                        <Link to="/pricing" onClick={handleServiceClick} data-testid="nav-pricing" className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                          <Tag className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm">Pricing</div>
                            <div className="text-xs text-gray-500 truncate">Implementation & advisory packages</div>
                          </div>
                        </Link>
                        <Link to="/co-design-studio" onClick={handleServiceClick} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" data-testid="nav-co-design-studio">
                          <Code className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm">ClinicIQ Co-Design Studio</div>
                            <div className="text-xs text-gray-500 truncate">Co-design &amp; custom development</div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem value="implementation-partner">
                  <NavigationMenuTrigger
                    data-testid="nav-implementation-partner"
                    className={`px-2.5 py-2 text-sm font-medium whitespace-nowrap transition-colors hover:text-orange-600 data-[state=open]:text-orange-600 ${isActive('partner') ? 'text-orange-600' : ''}`}
                  >
                    Implementation Partner
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-80 p-4">
                      <div className="grid gap-2">
                        <Link to="/senzcare-ai" onClick={handleServiceClick} data-testid="nav-senzcare-ai" className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                          <Brain className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm">SenzCare.AI</div>
                            <div className="text-xs text-gray-500 truncate">AI-powered elder care platform</div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    to="/about" 
                    onClick={() => setResourcesOpen(false)}
                    className={`inline-flex h-9 items-center px-2.5 text-sm font-medium whitespace-nowrap transition-colors hover:text-orange-600 ${
                      activeCls('about')
                    }`}
                  >
                    About Us
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Social Media Icons */}
          <div className="hidden 2xl:flex items-center space-x-3 mx-2 shrink-0">
            <a 
              href="https://www.linkedin.com/company/cliniciqca/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-blue-600 transition-colors"
              title="LinkedIn"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a 
              href="https://x.com/cliniciq" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-black transition-colors"
              title="X (Twitter)"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a 
              href="#instagram" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-pink-600 transition-colors"
              title="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
          </div>

          {/* Search and Auth Section */}
          <div className="flex items-center space-x-4">
            {/* Quick Search */}
            <div className="hidden sm:block relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search solutions"
                  className="w-48 xl:w-40 pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  data-testid="navbar-search-input"
                  value={navQuery}
                  onChange={(e) => setNavQuery(e.target.value)}
                  onFocus={() => setNavFocus(true)}
                  onBlur={() => setNavFocus(false)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && navQuery.trim()) {
                      navigate(`/marketplace?q=${encodeURIComponent(navQuery.trim())}&search_all=true`);
                      setNavFocus(false);
                    }
                  }}
                />
                <SearchSuggestions
                  query={navQuery}
                  open={navFocus}
                  testIdPrefix="navbar-search-suggest"
                  onPick={() => { setNavFocus(false); setNavQuery(''); }}
                />
              </div>
            </div>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-orange-600 text-white">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-blue-700 text-white text-sm">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium text-sm">Dr. {user?.first_name} {user?.last_name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <span className="text-purple-600 font-medium">Admin Panel</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    My Reviews
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-1 sm:space-x-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="px-2 sm:px-3">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white px-2.5 sm:px-3">
                    Register
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-toggle"
              className="xl:hidden p-1.5 sm:p-2 shrink-0 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden border-t border-gray-200 bg-white max-h-[calc(100vh-80px)] max-h-[calc(100dvh-80px)] overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]" data-testid="mobile-menu-panel">
            <div className="px-2 py-2">
              {/* Mobile Search */}
              <div className="px-2 py-1.5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search all solutions"
                    className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    data-testid="mobile-search-input"
                    value={mobileQuery}
                    onChange={(e) => setMobileQuery(e.target.value)}
                    onFocus={() => setMobileFocus(true)}
                    onBlur={() => setMobileFocus(false)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && mobileQuery.trim()) {
                        navigate(`/marketplace?q=${encodeURIComponent(mobileQuery.trim())}&search_all=true`);
                        setMobileMenuOpen(false);
                      }
                    }}
                  />
                  <SearchSuggestions
                    query={mobileQuery}
                    open={mobileFocus}
                    testIdPrefix="mobile-search-suggest"
                    onPick={() => { setMobileMenuOpen(false); setMobileQuery(''); }}
                  />
                </div>
              </div>
              <Link
                to="/marketplace"
                className={`block px-3 py-1.5 text-base font-medium hover:text-orange-600 hover:bg-gray-50 rounded-md ${activeCls('marketplace')}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Marketplace
              </Link>
              
              {/* Resources Section for Mobile */}
              <div className="border-t border-gray-100 mt-1 pt-1">
                <div className={`px-3 py-1 text-xs font-semibold uppercase tracking-wide ${isActive('clinics') ? 'text-orange-600' : 'text-gray-500'}`}>
                  For Clinics
                </div>
                <div className="pl-2">
                  <Link 
                    to="/selection-guide" 
                    data-testid="mobile-nav-clinics-selection-guide"
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg bg-orange-50 hover:bg-orange-100 ${location.pathname === '/selection-guide' ? 'ring-1 ring-orange-400 text-orange-700' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <BarChart3 className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">Technology Selection Guide</div>
                      <div className="text-xs text-gray-500">6 quick questions, matched tools</div>
                    </div>
                  </Link>
                  <Link 
                    to="/resources-guide" 
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg ${location.pathname === '/resources-guide' ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'}`} aria-current={location.pathname === '/resources-guide' ? 'page' : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <FileText className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">Medical Data Storage Guide</div>
                      <div className="text-xs text-gray-500">Storage solutions & compliance</div>
                    </div>
                  </Link>
                  <Link 
                    to="/data-migration" 
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg ${location.pathname === '/data-migration' ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'}`} aria-current={location.pathname === '/data-migration' ? 'page' : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ArrowRightLeft className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">EMR Data Migration Guide</div>
                      <div className="text-xs text-gray-500">Migration types & best practices</div>
                    </div>
                  </Link>
                  <Link 
                    to="/hl7-integration" 
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg ${location.pathname === '/hl7-integration' ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'}`} aria-current={location.pathname === '/hl7-integration' ? 'page' : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Network className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">HL7 & FHIR Integration</div>
                      <div className="text-xs text-gray-500">Standards-based EMR integration</div>
                    </div>
                  </Link>
                  <Link 
                    to="/cloud-deployment" 
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg ${location.pathname === '/cloud-deployment' ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'}`} aria-current={location.pathname === '/cloud-deployment' ? 'page' : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Cloud className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">On-Premise vs Hosted Cloud</div>
                      <div className="text-xs text-gray-500">Private cloud deployment options</div>
                    </div>
                  </Link>
                  <Link 
                    to="/privacy-security" 
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg ${location.pathname === '/privacy-security' ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'}`} aria-current={location.pathname === '/privacy-security' ? 'page' : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Shield className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">Privacy & Security Guide</div>
                      <div className="text-xs text-gray-500">Protecting health information</div>
                    </div>
                  </Link>
                </div>
              </div>

              <Link
                to="/for-vendors"
                className={`block px-3 py-1.5 text-base font-medium hover:text-orange-600 hover:bg-gray-50 rounded-md ${activeCls('vendors')}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                For Vendors
              </Link>

              {/* Services Section for Mobile */}
              <div className="border-t border-gray-100 mt-1 pt-1">
                <div className={`px-3 py-1 text-xs font-semibold uppercase tracking-wide ${isActive('services') ? 'text-orange-600' : 'text-gray-500'}`}>
                  Services
                </div>
                <div className="pl-2">
                  <Link
                    to="/pricing"
                    data-testid="mobile-nav-pricing"
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg ${location.pathname === '/pricing' ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'}`} aria-current={location.pathname === '/pricing' ? 'page' : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Tag className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">Pricing</div>
                      <div className="text-xs text-gray-500">Implementation & advisory packages</div>
                    </div>
                  </Link>
                  <Link 
                    to="/co-design-studio" 
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg ${location.pathname === '/co-design-studio' ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'}`} aria-current={location.pathname === '/co-design-studio' ? 'page' : undefined}
                    onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-co-design-studio"
                  >
                    <Code className="w-4 h-4 text-orange-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">ClinicIQ Co-Design Studio</div>
                      <div className="text-xs text-gray-500">Co-design &amp; custom development</div>
                    </div>
                  </Link>
                </div>
              </div>

              {/* Implementation Partner Section for Mobile */}
              <div className="border-t border-gray-100 mt-1 pt-1">
                <div className={`px-3 py-1 text-xs font-semibold uppercase tracking-wide ${isActive('partner') ? 'text-orange-600' : 'text-gray-500'}`}>
                  Implementation Partner
                </div>
                <div className="pl-2">
                  <Link 
                    to="/senzcare-ai" 
                    data-testid="mobile-nav-senzcare-ai"
                    className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg ${location.pathname === '/senzcare-ai' ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'}`} aria-current={location.pathname === '/senzcare-ai' ? 'page' : undefined}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Brain className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">SenzCare.AI</div>
                      <div className="text-xs text-gray-500">AI-powered elder care platform</div>
                    </div>
                  </Link>
                </div>
              </div>

              <Link
                to="/about"
                className={`block px-3 py-1.5 text-base font-medium hover:text-orange-600 hover:bg-gray-50 rounded-md ${activeCls('about')}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                About Us
              </Link>

              {isAuthenticated && (
                <Link
                  to="/dashboard"
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-orange-600 hover:bg-gray-50 rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              )}

              {/* Social Media Links for Mobile */}
              <div className="border-t border-gray-100 mt-2 pt-3">
                <div className="flex items-center justify-center space-x-6">
                  <a 
                    href="https://www.linkedin.com/company/cliniciqca/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-blue-600 transition-colors"
                    title="LinkedIn"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Linkedin className="w-6 h-6" />
                  </a>
                  <a 
                    href="https://x.com/cliniciq" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-black transition-colors"
                    title="X (Twitter)"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  <a 
                    href="#instagram" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-pink-600 transition-colors"
                    title="Instagram"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Instagram className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;